import { Component, HostListener, Input, NgZone } from '@angular/core';
import * as L from 'leaflet';
import { translate as t } from '@ngneat/transloco';
import { Location } from './location.model';

const DEBOUNCE_TIME = 200;
const MARKER_SIZE: L.PointExpression = [38, 38];
const DEFAULT_ICON_PATH = '/assets/icons/icon-map-marker-deselected.svg';
const SELECTED_ICON_PATH = '/assets/icons/icon-map-marker-selected.svg';

@Component({
  selector: 'app-world-map',
  templateUrl: './world-map.component.html',
  styleUrls: ['./world-map.component.scss']
})
export class WorldMapComponent {
  map!: L.Map;
  markers: L.Marker[] = [];
  top = 54.0;
  right = -45.0;
  bottom = -49.0;
  left = -122.0;
  resizeTimer: any;
  @Input() locations: Location[] = [];

  // The injected NgZone service provides a way to run a function outside the Angular zone
  constructor(private zone: NgZone) {}

  // ngOnInit is called when Angular is done initializing the component
  ngOnInit(): void {
    this.initMap();
  }

  // HostListener decorator allows us to subscribe to the window resizing event
  @HostListener('window:resize', [])
  onResize(): void {
    if (!this.map) return;
    this.resizeWithDelay();
  }

  /**
   * Initializes the Leaflet map.
   */
  initMap(): void {
    this.map = L.map('map', {
      attributionControl: false,
      boxZoom: false,
      doubleClickZoom: false,
      dragging: false,
      keyboard: false,
      scrollWheelZoom: false,
      tap: false,
      touchZoom: false,
      zoomControl: false,
      zoomSnap: 0.01
    });

    // Add a tile layer to the map. OpenStreetMap provides an open source option
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
    }).addTo(this.map);

    // Add all markers to the map
    this.setupMarkers();

    // Update the map to ensure the furthest edges of the max bounds are visible
    this.resizeWithDelay();
  }

  /**
   * Immediately resizes the map according to its container size.
   * @private
   */
  resizeNow(): void {
    this.zone.runOutsideAngular(() => {
      this.map.invalidateSize();
    });

    this.map.fitBounds(this.getMaxBounds());
  }

  /**
   * Debounced resize function to prevent rapid calling of map resizing.
   * @private
   */
  private resizeWithDelay(): void {
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(this.resizeNow.bind(this), DEBOUNCE_TIME);
  }

  /**
   * Returns the maximum geographical boundary based on defined top, right, bottom, and left coordinates.
   * @private
   */
  private getMaxBounds(): L.LatLngBounds {
    return L.latLngBounds(L.latLng(this.bottom, this.left), L.latLng(this.top, this.right));
  }

  /**
   * Initializes and sets up the markers on the map.
   * @private
   */
  private setupMarkers(): void {
    const defaultIcon: L.Icon = L.icon({
      iconUrl: DEFAULT_ICON_PATH,
      iconSize: MARKER_SIZE
    });
    const selectedIcon: L.Icon = L.icon({
      iconUrl: SELECTED_ICON_PATH,
      iconSize: MARKER_SIZE
    });

    this.locations.forEach((loc: any): void => {
      if (!loc || !loc.name) {
        return;
      }

      // Marker position
      const coords: L.LatLngExpression = L.latLng(loc.lat, loc.lng);

      let content =
        (loc.img ? `<img alt='' class='tooltip-location-img' src='${loc.img}' />` : '') +
        `
          <div class='tooltip-location-details'>
          <h6 class='location-name'>${loc.name}</h6>
        ` +
        (loc.established ? `<p class='text-x-small subtitle'>${t('components.world-map.established')} ${loc.established}</p>` : '') +
        `<p class='text-x-small subtitle'>${loc.employees} ${t('components.world-map.employees')}</p>`;

      if (loc.size) {
        content += `<p class='text-x-small subtitle'>${loc.size} m²</p>`;
      }

      content += `
        <a class='x-small job-openings' href='${t('components.world-map.link')}'>${t('components.world-map.jobs-link')}</a>
        </div>
      `;

      // Marker popup/tooltip
      const popupOptions: L.PopupOptions = {
        offset: [150, 150], // Arbitrary offset values to move popup to right-hand-side of marker
        className: 'custom-map-tooltip',
        content: content
      };
      const popup: L.Popup = L.popup(popupOptions).on('remove', () => {
        // Event called on popup/tooltip close
        this.resizeNow();
        this.markers.forEach((marker: L.Marker): void => {
          marker.setIcon(defaultIcon);
        });
      });

      // Marker creation
      const markerOptions: L.MarkerOptions = {
        icon: defaultIcon,
        title: loc.name
      };
      const marker: L.Marker = new L.Marker(coords, markerOptions)
        .bindPopup(popup)
        .on('popupopen', (): void => {
          // Event called on popup/tooltip open
          marker.setIcon(selectedIcon);
        })
        .addTo(this.map);
      this.markers.push(marker);
    });
  }
}
